import {
  commandRequest,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import type { UiCommandKey } from '../../../agents/context/result-action-commands.js';
import { Flame, AlertTriangle, Mail, CalendarCheck, TrendingUp, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

export interface SlashCommand {
  id: string;
  command: string;
  /**
   * ADR-0006 step 2: the stable registry command this entry dispatches. The
   * localized `prompt` is only fallback text for the chat history; the command
   * id is the dispatch key, so a locale switch never changes the action.
   */
  commandKey: UiCommandKey;
  aliases: string[];
  icon: typeof Flame;
  iconBg: string;
  title: string;
  desc: string;
  action: UiCommandRequest;
}

interface SlashCommandMenuProps {
  query: string;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
}

export function SlashCommandMenu({
  query,
  onSelect,
  onClose,
}: SlashCommandMenuProps): ReactElement | null {
  useI18nSubscription();
  const locale = i18n.locale;
  const [selectedIndex, setSelectedIndex] = useState(0);

  const allCommands = useMemo<SlashCommand[]>(
    () => [
      {
        id: 'leads',
        command: '/leads',
        commandKey: 'scenarioHotLeads',
        aliases: ['/лиды', '/hot', '/leads', '/горячие'],
        icon: Flame,
        iconBg: 'bg-red-100 text-red-600',
        title: i18n.t('crm.ai_chat.slash.lead'),
        desc: i18n.t('crm.ai_chat.welcome.hot_leads_desc'),
        action: commandRequest('scenarioHotLeads'),
      },
      {
        id: 'risk',
        command: '/risk',
        commandKey: 'scenarioDealRisks',
        aliases: ['/риск', '/риски', '/risk', '/аудит'],
        icon: AlertTriangle,
        iconBg: 'bg-amber-100 text-amber-700',
        title: i18n.t('crm.ai_chat.slash.risk'),
        desc: i18n.t('crm.ai_chat.welcome.pipeline_audit_desc'),
        action: commandRequest('scenarioDealRisks'),
      },
      {
        id: 'email',
        command: '/email',
        commandKey: 'scenarioFollowUp',
        aliases: ['/письмо', '/email', '/followup', '/фоллоуап'],
        icon: Mail,
        iconBg: 'bg-blue-100 text-blue-600',
        title: i18n.t('crm.ai_chat.slash.email'),
        desc: i18n.t('crm.ai_chat.welcome.follow_up_desc'),
        action: commandRequest('scenarioFollowUp'),
      },
      {
        id: 'task',
        command: '/task',
        commandKey: 'scenarioDailyPlan',
        aliases: ['/задачи', '/задача', '/task', '/план'],
        icon: CalendarCheck,
        iconBg: 'bg-emerald-100 text-emerald-700',
        title: i18n.t('crm.ai_chat.slash.task'),
        desc: i18n.t('crm.ai_chat.welcome.manager_brief_desc'),
        action: commandRequest('scenarioDailyPlan'),
      },
      {
        id: 'brief',
        command: '/brief',
        commandKey: 'executiveBrief',
        aliases: ['/сводка', '/brief', '/роп', '/отчет'],
        icon: TrendingUp,
        iconBg: 'bg-cyan-100 text-cyan-700',
        title: i18n.t('crm.ai_chat.slash.brief'),
        desc: i18n.t('crm.ai_chat.welcome.manager_brief_desc'),
        action: commandRequest('executiveBrief'),
      },
    ],
    [locale],
  );

  const filtered = useMemo(() => {
    const raw = query.toLowerCase().trim();
    if (!raw || raw === '/') return allCommands;
    const clean = raw.startsWith('/') ? raw : `/${raw}`;
    return allCommands.filter(
      (cmd) =>
        cmd.command.toLowerCase().includes(clean) ||
        cmd.aliases.some((a) => a.toLowerCase().includes(clean)) ||
        cmd.title.toLowerCase().includes(raw) ||
        cmd.desc.toLowerCase().includes(raw),
    );
  }, [allCommands, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filtered.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (filtered.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (filtered[selectedIndex]) {
          e.preventDefault();
          onSelect(filtered[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filtered, selectedIndex, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      data-testid="crm-slash-command-menu"
      className="absolute bottom-full left-0 right-0 mb-2 z-50 rounded-xl border border-stone-200 bg-white/95 p-1.5 shadow-lg backdrop-blur-md transition-all space-y-1"
    >
      <div className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-400">
        <Sparkles size={11} className="text-amber-500" />
        {i18n.t('crm.ai_chat.slash.title')}
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5 crm-microscroll">
        {filtered.map((cmd, idx) => {
          const IconComp = cmd.icon;
          const isSelected = idx === selectedIndex;
          return (
            <button
              key={cmd.id}
              type="button"
              onMouseEnter={() => setSelectedIndex(idx)}
              onClick={() => onSelect(cmd)}
              className={`flex w-full cursor-pointer items-center justify-between gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-xs transition ${
                isSelected
                  ? 'bg-amber-50 text-amber-950 font-semibold border border-amber-200/90'
                  : 'text-stone-700 hover:bg-stone-50 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${cmd.iconBg}`}
                >
                  <IconComp size={13} />
                </span>
                <span className="font-mono text-[11px] font-bold text-amber-800 shrink-0">
                  {cmd.command}
                </span>
                <span className="truncate text-xs text-stone-900">{cmd.title}</span>
              </div>
              <span className="shrink-0 text-[10px] text-stone-400 font-normal">↵ Enter</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
